import React, {useCallback, useRef, useEffect} from 'react';
import styled from 'styled-components/native';
import {
  TouchableOpacity,
  StyleSheet,
  Animated,
  Easing,
  Platform,
} from 'react-native';
import Text from '../textGeneric';
import icons from '../../assets/icons/adapter';
import {IconsProps} from '../../interfaces/components/icons';
import MyPressable from '../myPressable';

const Label = styled(Text)`
  font-size: ${({theme}) => theme.text.s9}px;
  color: ${({theme}) => theme.primary};
  margin-left: 10px;
`;

const Row = styled.View`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
`;

const Ripple = styled(MyPressable)`
  width: 40px;
  height: 40px;
  border-radius: 20px;
  flex-direction: row;
  align-items: center;
  justify-content: center;
`;

const styles = StyleSheet.create({
  center: {
    marginTop: 'auto',
    marginBottom: 'auto',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
});

/**
 * Icon Component
 * A versatile icon component that supports various icon types and behaviors.
 *
 * @component
 * @example
 * return (
 *   <Icon name="check"  onPress={handleIconPress} />
 * )
 *
 * @param {IconsProps} props - The icon component props.
 * @param {string} props.name - The name of the icon.
 * @param {string} [props.label] - The label to be displayed next to the icon.
 * @param {Function} [props.onPress] - The event handler for icon press.
 * @param {object} [props.style] - The style object for the icon container.
 * @param {string} [props.role] - The role of the icon (button, decorative, etc.).
 * @param {boolean} [props.loading] - Whether the icon should be in loading state.
 * @param {object} [props.props] - Additional props to be passed to the icon component.
 *
 * @returns {React.ReactElement} The rendered icon component.
 */
const Icon = ({
  name,
  label,
  onPress,
  style,
  role,
  loading,
  ...props
}: IconsProps): React.ReactElement => {
  const spinValue = useRef(new Animated.Value(0)).current;

  /**
   * Starts the rotation animation for the loading state.
   */
  const spin = useCallback(() => {
    if (loading) {
      spinValue.setValue(0);

      Animated.timing(spinValue, {
        toValue: 1,
        duration: 1500,
        easing: Easing.linear,
        useNativeDriver: Platform.OS !== 'web',
      }).start(() => spin());
    }
  }, [loading, spinValue]);

  useEffect(() => {
    if (loading) {
      spin();
    }
  }, [loading, spin]);

  const rotate = spinValue.interpolate({
    inputRange: [0, 1],
    outputRange: ['0deg', '360deg'],
  });

  // Combine all styles that will be applied to icons
  const iconStyles = [
    !label && !onPress && style,
    styles.center,
    loading && {transform: [{rotate}]},
  ];

  /**
   * Renders the appropriate icon based on the provided type.
   *
   * @returns {React.ReactElement} The rendered icon element.
   */
  const icon = useCallback(() => {
    if (!icons[name as keyof typeof icons]) {
      return <></>;
    }
    return (
      <>
        {React.cloneElement(icons[name as keyof typeof icons], {
          width: props.width || props.size,
          height: props.height || props.size,
          fill: props.color,
          stroke: props.stroke || props.color,
          style,
        })}
      </>
    );
  }, [name, props, style, loading, iconStyles]);

  if (role === 'button') {
    return (
      <Ripple borderRadius={24} style={style} onPress={onPress}>
        {icon()}
      </Ripple>
    );
  }

  if (onPress) {
    return (
      <TouchableOpacity style={style} onPress={onPress}>
        {icon()}
      </TouchableOpacity>
    );
  }

  if (label) {
    return (
      <Row style={style}>
        {icon()}
        <Label>{label}</Label>
      </Row>
    );
  }

  return <>{icon()}</>;
};

export default Icon;
